// src/components/Admin/AdminSidebar.tsx
import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import type { RootState } from '../../store';
import { 
  LayoutDashboard, 
  Users, 
  FileText, 
  BarChart3, 
  Settings, 
  Shield, 
  Radio, 
  Menu, 
  X,
  ChevronRight,
  Home,
  Eye,
  UserCheck,
  AlertTriangle,
  FileVideo,
  Image,
  Headphones,
  Film,
  Zap,
  Bell,
  Mail,
  Database,
  List,
  Clapperboard,
  Briefcase
} from 'lucide-react';

interface AdminSidebarProps {
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  isMobile?: boolean;
  onCloseMobile?: () => void;
}

interface NavItem {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  path?: string;
  children?: NavItem[];
}

const AdminSidebar: React.FC<AdminSidebarProps> = ({ 
  isCollapsed, 
  onToggleCollapse, 
  isMobile = false,
  onCloseMobile 
}) => {
  const location = useLocation();
  const [expandedItems, setExpandedItems] = useState<string[]>(['content']);
  const { userInfo } = useSelector((state: RootState) => state.user);

  /**
   * Staff see only the areas they can act in.
   *
   * Hiding a section is not the control - the backend refuses a staff account on every
   * other endpoint, and that is what actually protects them. This is so somebody is not
   * offered Content Management, clicks it, and gets a wall of failures with no
   * explanation. The sidebar should describe what the person can do.
   */
  const u = userInfo as unknown as Record<string, unknown> | null;
  const isAdmin = Boolean(u?.['isAdmin'] ?? u?.['admin']);
  const isStaffOnly = !isAdmin && Boolean(u?.['isStaff']);

  /** The three things staff exist for, plus the read-only views they are allowed. */
  const STAFF_SECTIONS = ['dashboard', 'members', 'casting', 'productions', 'analytics'];

  const navigationItems: NavItem[] = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: LayoutDashboard,
      path: '/admin'
    },
    {
      id: 'users',
      label: 'User Management',
      icon: Users,
      children: [
        { id: 'all-users', label: 'All Users', icon: Users, path: '/admin/users' },
        { id: 'admins', label: 'Admin Users', icon: UserCheck, path: '/admin/users/admins' },
        { id: 'pending-verification', label: 'Pending Verification', icon: AlertTriangle, path: '/admin/users/pending' },
        { id: 'team-positions', label: 'Team Positions', icon: Briefcase, path: '/admin/users/positions' }
      ]
    },
    {
      id: 'content',
      label: 'Content Management',
      icon: FileText,
      children: [
        { id: 'all-content', label: 'All Content', icon: FileText, path: '/admin/content' },
        { id: 'cinema', label: 'Cinema', icon: Film, path: '/admin/content/cinema' },
        { id: 'contents', label: 'Contents', icon: FileText, path: '/admin/content/contents' },
        { id: 'podcasts', label: 'Podcasts', icon: Headphones, path: '/admin/content/podcasts' },
        { id: 'animations', label: 'Animations', icon: Image, path: '/admin/content/animations' },
        { id: 'sneak-peeks', label: 'Sneak Peeks', icon: Eye, path: '/admin/content/sneak-peeks' },
        { id: 'live-videos', label: 'Live Videos', icon: Radio, path: '/admin/content/live-videos' }
      ]
    },
    {
      id: 'casting',
      label: 'Casting',
      icon: Users,
      path: '/admin/casting'
    },
    {
      id: 'members',
      label: 'Community Members',
      icon: UserCheck,
      path: '/admin/members'
    },
    {
      id: 'productions',
      label: 'Productions',
      icon: Clapperboard,
      path: '/admin/productions'
    },
    {
      id: 'live-streams',
      label: 'Live Streaming',
      icon: Radio,
      path: '/admin/live-streams'
    },
    {
      id: 'series',
      label: 'Series Management',
      icon: List,
      children: [
        { id: 'all-series', label: 'All Series', icon: List, path: '/admin/series' },
        { id: 'cinema-series', label: 'Cinema Series', icon: Film, path: '/admin/series/cinema' },
        { id: 'content-series', label: 'Content Series', icon: FileText, path: '/admin/series/contents' },
        { id: 'podcast-series', label: 'Podcast Series', icon: Headphones, path: '/admin/series/podcasts' },
        { id: 'animation-series', label: 'Animation Series', icon: Image, path: '/admin/series/animations' }
      ]
    },
    {
      id: 'moderation',
      label: 'Comment Moderation',
      icon: Shield,
      path: '/admin/moderation'
    },
    {
      id: 'analytics',
      label: 'Analytics & Reports',
      icon: BarChart3,
      children: [
        { id: 'overview', label: 'Overview', icon: BarChart3, path: '/admin/analytics' },
        { id: 'user-analytics', label: 'User Analytics', icon: Users, path: '/admin/analytics/users' },
        { id: 'content-analytics', label: 'Content Analytics', icon: FileVideo, path: '/admin/analytics/content' },
        { id: 'engagement', label: 'Engagement Metrics', icon: Zap, path: '/admin/analytics/engagement' }
      ]
    },
    {
      id: 'communications',
      label: 'Communications',
      icon: Mail,
      children: [
        { id: 'email-campaigns', label: 'Email Campaigns', icon: Mail, path: '/admin/communications/email' },
        { id: 'notifications', label: 'Push Notifications', icon: Bell, path: '/admin/communications/notifications' }
      ]
    },
    {
      id: 'system',
      label: 'System',
      icon: Settings,
      children: [
        { id: 'settings', label: 'System Settings', icon: Settings, path: '/admin/system/settings' },
        { id: 'backup', label: 'Backup & Recovery', icon: Database, path: '/admin/system/backup' },
        { id: 'logs', label: 'System Logs', icon: FileText, path: '/admin/system/logs' }
      ]
    }
  ];

  /**
   * What this person is shown. An admin sees everything; staff see their five sections in
   * the order they appear above, so the panel reads the same for both.
   */
  const visibleItems = isStaffOnly
    ? navigationItems.filter((item) => STAFF_SECTIONS.includes(item.id))
    : navigationItems;

  const toggleExpand = (itemId: string) => {
    setExpandedItems(prev => 
      prev.includes(itemId) 
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const isParentActive = (children: NavItem[]) => {
    return children.some(child => child.path && isActive(child.path));
  };

  const renderNavItem = (item: NavItem, level = 0) => {
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedItems.includes(item.id);
    const ItemIcon = item.icon;
    const shouldShowText = !isCollapsed || isMobile;

    if (hasChildren) {
      return (
        <div key={item.id} className="mb-1">
          <button
            onClick={() => toggleExpand(item.id)}
            className={`
              w-full flex items-center justify-between px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-150
              ${isParentActive(item.children!) 
                ? 'bg-orange-600 text-white' 
                : 'text-gray-300 hover:text-white hover:bg-slate-700'
              }
              ${!shouldShowText ? 'justify-center' : ''}
            `}
          >
            <div className="flex items-center">
              <ItemIcon className={`h-5 w-5 ${shouldShowText ? 'mr-3' : ''}`} />
              {shouldShowText && <span className="truncate">{item.label}</span>}
            </div>
            {shouldShowText && (
              <ChevronRight 
                className={`h-4 w-4 transition-transform duration-200 flex-shrink-0 ${isExpanded ? 'rotate-90' : ''}`} 
              />
            )}
          </button>
          
          {shouldShowText && isExpanded && (
            <div className="ml-4 mt-1 space-y-1">
              {item.children!.map(child => renderNavItem(child, level + 1))}
            </div>
          )}
        </div>
      );
    }

    return (
      <Link
        key={item.id}
        to={item.path!}
        onClick={isMobile ? onCloseMobile : undefined}
        className={`
          flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-150 mb-1
          ${isActive(item.path!) 
            ? 'bg-orange-600 text-white' 
            : 'text-gray-300 hover:text-white hover:bg-slate-700'
          }
          ${level > 0 ? 'ml-0' : ''}
          ${!shouldShowText ? 'justify-center' : ''}
        `}
      >
        <ItemIcon className={`h-5 w-5 ${shouldShowText ? 'mr-3' : ''}`} />
        {shouldShowText && <span className="truncate">{item.label}</span>}
      </Link>
    );
  };

  const shouldShowText = !isCollapsed || isMobile;
  const sidebarWidth = isMobile ? 'w-80' : (isCollapsed ? 'w-16' : 'w-64');

  return (
    <div className={`
      bg-slate-800 border-r border-slate-700 flex flex-col transition-all duration-300 h-screen
      ${sidebarWidth}
    `}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-slate-700 min-h-[73px]">
        {shouldShowText && (
          <Link to="/" className="flex items-center min-w-0">
            <Home className="h-6 w-6 text-orange-500 mr-2 flex-shrink-0" />
            <span className="text-white font-bold text-lg truncate">StrymHub</span>
          </Link>
        )}
        <button
          onClick={onToggleCollapse}
          className="p-1.5 rounded-md text-gray-400 hover:text-white hover:bg-slate-700 transition-colors flex-shrink-0 ml-auto"
        >
          {isMobile ? (
            <X className="h-5 w-5" />
          ) : isCollapsed ? (
            <Menu className="h-5 w-5" />
          ) : (
            <X className="h-5 w-5" />
          )}
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 overflow-y-auto">
        {shouldShowText && (
          <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">
            Admin Panel
          </div>
        )}
        
        <div className="space-y-1">
          {visibleItems.map(item => renderNavItem(item))}
        </div>
      </nav>

      {/* Footer */}
      {shouldShowText && (
        <div className="p-4 border-t border-slate-700">
          <div className="text-xs text-gray-400 text-center">
            Admin Dashboard v1.0
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSidebar;